import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BuildIcon from '@mui/icons-material/Build';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import TuneIcon from '@mui/icons-material/Tune';
import SchoolIcon from '@mui/icons-material/School';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import SecurityIcon from '@mui/icons-material/Security';
import { Link } from 'react-router-dom';

const Iso31000: React.FC = () => {
  const implementationProcess = [
    {
      title: "Risk Context Analysis",
      description: "We assess your organization's internal and external context to understand risk factors and opportunities.",
      icon: <AssessmentIcon fontSize="large" />
    },
    {
      title: "Risk Framework Design",
      description: "We design a risk management framework tailored to your organization's specific needs and objectives.",
      icon: <TuneIcon fontSize="large" />
    },
    {
      title: "Risk Identification",
      description: "We help identify risks across all areas of your organization that could affect achievement of objectives.",
      icon: <SecurityIcon fontSize="large" />
    },
    {
      title: "Risk Assessment",
      description: "We assess risks in terms of likelihood and impact to prioritize risk treatment activities.",
      icon: <FactCheckIcon fontSize="large" />
    },
    {
      title: "Risk Treatment",
      description: "We develop risk treatment plans and strategies to address identified risks effectively.",
      icon: <BuildIcon fontSize="large" />
    },
    {
      title: "Documentation",
      description: "We create comprehensive risk management documentation including policies, procedures, and risk registers.",
      icon: <FactCheckIcon fontSize="large" />
    },
    {
      title: "Training",
      description: "We train your team on risk management principles, methodologies, and their roles in the process.",
      icon: <SchoolIcon fontSize="large" />
    },
    {
      title: "Monitoring & Review",
      description: "We help establish processes for ongoing monitoring, review, and continuous improvement of risk management.",
      icon: <AutorenewIcon fontSize="large" />
    }
  ];

  const benefits = [
    "Improved decision-making through better risk understanding",
    "Enhanced ability to identify opportunities and threats",
    "Better allocation of resources to manage risks effectively",
    "Increased stakeholder confidence and trust",
    "Compliance with regulatory and governance requirements",
    "Reduced uncertainty and improved predictability",
    "Better protection of assets and reputation",
    "Enhanced organizational resilience and adaptability",
    "Improved strategic planning and goal achievement",
    "Competitive advantage through effective risk management"
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          position: 'relative',
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              ISO 31000:2018 Risk Management
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Comprehensive risk management framework implementation to enhance decision-making and organizational resilience
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Overview Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 6,
          alignItems: 'center' 
        }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                What is ISO 31000:2018?
              </Typography>
              <Typography paragraph>
                ISO 31000:2018 is the international standard for Risk Management. It provides principles, framework, 
                and a process for managing risk that can be used by any organization, regardless of its size, activity, 
                or sector.
              </Typography>
              <Typography paragraph>
                This standard helps organizations create and protect value by managing risks, making informed decisions, 
                and achieving objectives. It emphasizes the importance of risk management being integrated into all 
                organizational activities and decision-making processes.
              </Typography>
              <Typography paragraph>
                ISO 31000 provides a common approach to risk management that can be applied across different types of 
                risks and in different contexts, helping organizations improve their ability to identify opportunities 
                and threats.
              </Typography>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="Risk Management"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Implementation Process Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
              sx={{ mb: 2 }}
            >
              Our ISO 31000 Implementation Process
            </Typography>
            <Typography 
              variant="body1" 
              paragraph 
              align="center" 
              sx={{ maxWidth: '800px', mx: 'auto', mb: 6 }}
            >
              We follow a structured approach to implement ISO 31000:2018 that ensures effective risk management
            </Typography>
          </motion.div>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(4, 1fr)' 
            },
            gap: 4 
          }}>
            {implementationProcess.map((step, index) => (
              <Box key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: '16px',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-10px)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    <Box sx={{
                      width: 60,
                      height: 60,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      backgroundColor: '#7b1fa215',
                      color: '#7b1fa2',
                      mb: 3,
                    }}>
                      {step.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      component="h3"
                      gutterBottom
                      fontWeight="bold"
                      color="#0c2340"
                    >
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.description}
                    </Typography>
                  </Paper>
                </motion.div>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
            sx={{ mb: 2 }}
          >
            Benefits of ISO 31000 Implementation
          </Typography>
          <Typography 
            variant="body1" 
            paragraph 
            align="center" 
            sx={{ maxWidth: '800px', mx: 'auto', mb: 6 }}
          >
            Implementing ISO 31000 provides numerous benefits that help organizations make better decisions and manage risks effectively
          </Typography>
        </motion.div>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                }}
              >
                <List>
                  {benefits.slice(0, 5).map((benefit, index) => (
                    <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutlineIcon sx={{ color: '#7b1fa2' }} />
                      </ListItemIcon>
                      <ListItemText primary={benefit} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                }}
              >
                <List>
                  {benefits.slice(5).map((benefit, index) => (
                    <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutlineIcon sx={{ color: '#7b1fa2' }} />
                      </ListItemIcon>
                      <ListItemText primary={benefit} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Call to Action Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 6,
            alignItems: 'center' 
          }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <Typography 
                  variant="h4" 
                  component="h2" 
                  gutterBottom 
                  fontWeight="bold"
                  color="#0c2340"
                >
                  Ready to Enhance Your Risk Management?
                </Typography>
                <Typography paragraph>
                  Implementing ISO 31000:2018 is a strategic decision that helps your organization make better decisions, 
                  identify opportunities, and manage risks effectively.
                </Typography>
                <Typography paragraph sx={{ mb: 4 }}>
                  Contact us today to learn how we can help you implement an effective risk management framework 
                  that enhances your organizational resilience.
                </Typography>
                <Button 
                  variant="contained" 
                  sx={{ 
                    bgcolor: '#7b1fa2',
                    '&:hover': { bgcolor: '#6a1b9a' },
                    mt: 2,
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px',
                    fontWeight: 'bold'
                  }}
                  size="large"
                  component={Link}
                  to="/contact?service=iso31000"
                >
                  Request a Free Consultation
                </Button>
              </motion.div>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <Box 
                  component="img"
                  src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                  alt="Risk Management Framework"
                  sx={{
                    width: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  }}
                />
              </motion.div>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Iso31000;
